import { defineField, defineType } from 'sanity'
import TireSizesSelector from '../../components/TireSizesSelector'

export default defineType({
  name: 'tireModel',
  title: 'Tire Models',
  type: 'document',
  fields: [
    defineField({
      name: 'brand',
      title: 'Brand',
      type: 'reference',
      to: [{ type: 'tireBrand' }],
      validation: (Rule) => Rule.required(),
      options: {
        disableNew: true,
      },
    }),
    defineField({
      name: 'name',
      title: 'Name',
      type: 'string',
      validation: (Rule) => Rule.required(),
    }),
    defineField({
      name: 'type',
      title: 'Tire Type',
      type: 'reference',
      to: [{ type: 'tireType' }],
      validation: (Rule) => Rule.required(),
      options: {
        disableNew: true,
      },
    }),
    defineField({
      name: 'tireSizesConfig',
      title: 'Tire Sizes Configuration',
      type: 'object',
      components: {
        input: TireSizesSelector,
      },
      fields: [
        {
          name: 'defaultTireFormat',
          title: 'Default Tire Format',
          type: 'string',
          options: {
            list: [
              { title: 'Standard', value: 'standard' },
              { title: 'Racing Slick', value: 'slick' },
              { title: 'Off-road', value: 'offroad' },
            ],
          },
          validation: (Rule) => Rule.required(),
        },
        {
          name: 'sizes',
          title: 'Tire Sizes',
          type: 'array',
          of: [
            {
              type: 'object',
              fields: [
                {
                  name: 'tireSize',
                  title: 'Tire Size',
                  type: 'reference',
                  to: [{ type: 'tireSize' }],
                  validation: (Rule) => Rule.required(),
                },
                // Load & Speed Ratings
                {
                  name: 'sizeRating',
                  title: 'Size Rating (tire code)',
                  type: 'string',
                  description: 'The size rating/code for the tire',
                },
                {
                  name: 'loadIndex',
                  title: 'Load Index',
                  type: 'number',
                  description: 'Numeric load index value',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null) return true
                      return Number.isInteger(value) && value > 0
                        ? true
                        : 'Load index must be a positive integer'
                    }),
                },
                {
                  name: 'speedIndex',
                  title: 'Speed Index',
                  type: 'string',
                  description: 'Speed rating letter (e.g., H, V, W, Y)',
                },
                {
                  name: 'loadRange',
                  title: 'Load Range',
                  type: 'string',
                  description: 'Load range classification (e.g., SL, XL, E)',
                },
                {
                  name: 'maxLoad',
                  title: 'Max Load (lbs)',
                  type: 'number',
                  description: 'Maximum load capacity in pounds',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null) return true
                      return value > 0 ? true : 'Max load must be positive'
                    }),
                },
                {
                  name: 'maxInflationPressure',
                  title: 'Max Inflation Pressure (PSI)',
                  type: 'number',
                  description: 'Maximum inflation pressure in PSI',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null) return true
                      return value > 0 ? true : 'Max inflation pressure must be positive'
                    }),
                },
                // Physical Dimensions
                {
                  name: 'treadDepth',
                  title: 'Tread Depth (32nds)',
                  type: 'string',
                  description: 'Tread depth in 32nds of an inch (e.g., "7/32", "10.5/32")',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null || value === '')
                        return true
                      // Allow fraction format like "7/32" or "10.5/32", or decimal numbers
                      const fractionPattern = /^\d+(\.\d+)?\/\d+$/
                      const decimalPattern = /^\d+(\.\d+)?$/
                      return fractionPattern.test(value) || decimalPattern.test(value)
                        ? true
                        : 'Tread depth must be in fraction format (e.g., "7/32" or "10.5/32") or decimal number'
                    }),
                },
                {
                  name: 'tireWeight',
                  title: 'Tire Weight (lbs)',
                  type: 'number',
                  description: 'Weight of the tire in pounds',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null) return true
                      return value > 0 ? true : 'Tire weight must be positive'
                    }),
                },
                {
                  name: 'rimWidthRange',
                  title: 'Rim Width Range',
                  type: 'string',
                  description: 'Range of compatible rim widths (e.g., "7.0-8.5")',
                },
                {
                  name: 'measRimWidth',
                  title: 'Measuring Rim Width',
                  type: 'number',
                  description: 'Measuring rim width',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null) return true
                      return value > 0 ? true : 'Measuring rim width must be positive'
                    }),
                },
                {
                  name: 'sectWidth',
                  title: 'Section Width (mm)',
                  type: 'number',
                  description: 'Section width in millimeters',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null) return true
                      return value > 0 ? true : 'Section width must be positive'
                    }),
                },
                {
                  name: 'treadWidth',
                  title: 'Tread Width (mm)',
                  type: 'number',
                  description: 'Tread width in millimeters',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null) return true
                      return value > 0 ? true : 'Tread width must be positive'
                    }),
                },
                {
                  name: 'overallDiam',
                  title: 'Overall Diameter (inches)',
                  type: 'number',
                  description: 'Overall diameter in inches',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null) return true
                      return value > 0 ? true : 'Overall diameter must be positive'
                    }),
                },
                // Performance
                {
                  name: 'revsPerMile',
                  title: 'Revolutions Per Mile',
                  type: 'number',
                  description: 'Number of revolutions per mile',
                  validation: (Rule) =>
                    Rule.custom((value) => {
                      if (value === undefined || value === null) return true
                      return value > 0 ? true : 'Revolutions per mile must be positive'
                    }),
                },
                // Documentation
                {
                  name: 'countryOfOrigin',
                  title: 'Country of Origin',
                  type: 'string',
                  description: 'Country where tire is manufactured',
                },
                {
                  name: 'sourceUrl',
                  title: 'Source URL',
                  type: 'url',
                  description: 'URL source for specifications',
                  validation: (Rule) =>
                    Rule.uri({
                      allowRelative: false,
                      scheme: ['http', 'https'],
                    }),
                },
                {
                  name: 'internalNotes',
                  title: 'Internal Notes',
                  type: 'text',
                  description: 'Internal notes about the tire',
                },
                {
                  name: 'treadwear',
                  title: 'Treadwear',
                  type: 'reference',
                  to: [{ type: 'tireTreadwear' }],
                  description: 'Treadwear rating for this specific tire size',
                  options: {
                    disableNew: true,
                  },
                },
              ],
            },
          ],
        },
      ],
    }),
  ],
  orderings: [
    {
      title: 'Brand',
      name: 'brandAsc',
      by: [{ field: 'brand.name', direction: 'asc' }],
    },
    {
      title: 'Brand (Z-A)',
      name: 'brandDesc',
      by: [{ field: 'brand.name', direction: 'desc' }],
    },
  ],
  preview: {
    select: {
      name: 'name',
      brand: 'brand.name',
      type: 'type.name',
      sizes: 'tireSizesConfig.sizes',
    },
    prepare({ name, brand, type, sizes }) {
      const count = sizes?.length || 0

      return {
        title: name || 'Unnamed Tire Model',
        subtitle: `(${count} sizes) ${brand || 'No Brand'} | ${type || 'No Type'} `,
      }
    },
  },
})
